import React, {useState} from 'react';
import {Button, Modal, Spinner} from "@tag/tag-components-react-v4";
import {CustomerDTO} from "../types/CustomerDTO";
import Reset24hFileModal from "./Reset24hFileModal";
import AcpSettingsService from "../services/AcpSettingsService";
import {Moment} from "moment";
import DataExportModal from "./DataExportModal";
import styles from './CustomerMenuModal.module.scss'
import CustomerService from "../services/CustomerService";
import AppRolesService from "../../General/services/AppRolesService";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import RestoreLiveToTestModal from "./RestoreLiveToTestModal";
import MoveCustomerToAppGroupModal from "./MoveCustomerToAppGroupModal";
import RemoveCustomerModal from "./RemoveCustomerModal";
import { DisablePeoplePlannerModal } from './DisablePeoplePlannerModal';
import { DisableAppInsideAcrModal } from './DisableAppInsideAcrModal';
import { EnableCareRosteringModal } from './EnableCareRosteringModal/EnableCareRosteringModal';

function CustomerMenuModal(props: {
    selectedCustomer: CustomerDTO, 
    handleClose: Function, 
    setAlert: Function, 
    reloadCustomerData: Function,
    updateCustomerCareRosteringStatus?: (clientId: number, isEnabled: boolean) => Promise<void>
}) {

    const [reset24hFileVisible, setReset24hFileVisible] = useState(false);
    const [dataExportVisible, setDataExportVisible] = useState(false);
    const [removeModalVisible, setRemoveModalVisible] = useState(false);
    const [restoreLiveToTestVisible, setRestoreLiveToTestVisible] = useState(false);
    const [moveAppGrpVisible, setMoveAppGrpVisible] = useState(false);
    const [disablePeoplePlannerVisible, setDisablePeoplePlannerVisible] = useState(false);
    const [disableAppInsideAcrVisible, setDisableAppInsideAcrVisible] = useState(false);
    const [enableCareRosteringVisible, setEnableCareRosteringVisible] = useState(false);
    const [loading, setLoading] = useState<string|boolean>(false);
    
    const { accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    let canDeleteEnv = AppRolesService.canUserDeleteEnvironments(accounts, isAuthenticated);
    let canLockEnv = AppRolesService.canUserLockAppEnvironments(accounts, isAuthenticated);
    let isAdmin = AppRolesService.isUserAdmin(accounts, isAuthenticated);
    let canConfigureACR = AppRolesService.canUserConfigureACR(accounts, isAuthenticated);
    let canEnableACR = AppRolesService.canUserEnableACR(accounts, isAuthenticated);

    const delay = (ms:number) => new Promise(res => setTimeout(res, ms));

    const redirectToCustomersAPI = () => {
        window.open(`https://${props.selectedCustomer.serviceName}-${props.selectedCustomer.appgrp}.peopleplanner.biz/apidocumentation/#`, '_blank');
    }
    
    const updateACPSettings = (clientId: number, settingId: number, dateTimeToday: Moment) => {
        AcpSettingsService.updateAcp24HFileForCustomer(clientId, settingId, dateTimeToday)
            .then(() => {
                props.setAlert({type: 'success', message: 'ACP Settings Updated'});
            })
            .catch(() => {
                props.setAlert({type: 'error', message: 'An error has occured, please contact HSC Care DevOps'});
            })
            .then(() => {
                props.handleClose();
                props.reloadCustomerData();
            });
    }
    
    const deleteAPPCustomer = () => {
        if (!canDeleteEnv) {
            throw new Error()
        }
        
        setLoading(true);
        
        CustomerService.deleteCustomer(props.selectedCustomer.clientId)
            .then((result) => {
                props.setAlert({type: 'success', message: 'Customer Deleted'});
            })
            .catch(() => {
                props.setAlert({type: 'error', message: 'An error has occured, please contact HSC Care DevOps'});
            })
            .then(() => {
                setLoading(false)
                props.handleClose();
                props.reloadCustomerData();
            });
    }

    const handleLockAPPCustomer = async (salesForceId: string) => {
        let errorMessage = '';
        setLoading('Starting...');

        try {
            const requestLockResponse = await CustomerService.requestlockAppCustomer(props.selectedCustomer.clientId, salesForceId);
            
            const taskId = requestLockResponse.taskId;

            let status;
            do {
                status = await CustomerService.lockAppCustomerStatus(taskId);
                setLoading(status.progressMessage);
                await delay(50);
            } while (!status.complete && !status.errorMessage);
            
            if (status.errorMessage) {
                errorMessage = status.errorMessage;
                throw new Error();
            }

            props.setAlert({type: 'success', message: 'Customer Locked - Please view terminations page'});
        } catch (e) {
            props.setAlert({type: 'error', message: errorMessage || 'An error has occured, please contact HSC Care DevOps'});
        } finally {
            props.handleClose();
            props.reloadCustomerData();
            setLoading(false);
        }
    }
    
    let content;
    if (loading) {
        content = <Spinner full={true} >{loading}</Spinner>;
    }
    else {
        content = <>
            <Modal
                visible={!!props.selectedCustomer}
                accent="teal"
                heading={props.selectedCustomer?.name + ' - (' + props.selectedCustomer?.clientId + ')'}
                primaryButtonProps={{buttonStyle: {'display': 'none'}}}
                onCancelButtonClick={() => {
                    props.handleClose();
                }}
                onClose={() => props.handleClose()}
            >
                <div className={styles.CustomerMenuModalButtonContainer}>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Reset ACP 24h file'} onClick={() => setReset24hFileVisible(true)} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Data Export'} onClick={() => setDataExportVisible(true)} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Go To API'} onClick={() => redirectToCustomersAPI()} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Remove Tenant'} onClick={() => setRemoveModalVisible(true)} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Restore Live to Test'} disabled={!isAdmin} onClick={() => setRestoreLiveToTestVisible(true)} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button text={'Move'} disabled={!isAdmin} onClick={() => setMoveAppGrpVisible(true)} />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button
                            text={"Remove APP Option from EVO"}
                            disabled={!canConfigureACR}
                            onClick={() => setDisablePeoplePlannerVisible(true)}
                        />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button
                            text={"Remove APP Button from ACR"}
                            disabled={!canConfigureACR}
                            onClick={() => setDisableAppInsideAcrVisible(true)}
                        />
                    </div>
                    <div className={styles.CustomerMenuModalButtonElement}>
                        <Button
                            text={"Enable Care Rostering"}
                            disabled={!canEnableACR}
                            onClick={() => setEnableCareRosteringVisible(true)}
                        />
                    </div>
                </div>
            </Modal>
        </>
    }

    return (
        <>
            {content}
            {reset24hFileVisible ?
                <Reset24hFileModal
                    visible={reset24hFileVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setReset24hFileVisible(false)}
                    updateACPSettings={(clientId: number, settingId: number, dateTimeToday: Moment) =>
                        updateACPSettings(clientId, settingId, dateTimeToday)
                    }
                /> : undefined
            }
            {dataExportVisible ?
                <DataExportModal
                    visible={dataExportVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setDataExportVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                    setAlert={props.setAlert}
                /> : undefined
            }
            {removeModalVisible ?
                <RemoveCustomerModal
                    visible={removeModalVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setRemoveModalVisible(false)}
                    handleDelete={deleteAPPCustomer}
                    handleLock={handleLockAPPCustomer}
                    canDeleteEnv={canDeleteEnv}
                    canLockEnv={canLockEnv}
                /> : undefined
            }
            {restoreLiveToTestVisible ?
                <RestoreLiveToTestModal
                    visible={restoreLiveToTestVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setRestoreLiveToTestVisible(false)}
                    setLoading={setLoading}
                    setAlert={props.setAlert}
                    handleCustomerMenuModalClose={props.handleClose}
                /> : undefined
            }
            {moveAppGrpVisible ?
                <MoveCustomerToAppGroupModal
                    visible={moveAppGrpVisible}
                    selectedCustomer={props.selectedCustomer}
                    handleClose={() => setMoveAppGrpVisible(false)}
                    setLoading={setLoading}
                    setAlert={props.setAlert}
                    handleCustomerMenuModalClose={props.handleClose}
                    reloadCustomerList={props.reloadCustomerData}
                /> : undefined
            }
            {disablePeoplePlannerVisible ?
                <DisablePeoplePlannerModal
                    visible={disablePeoplePlannerVisible}
                    selectedCustomer={props.selectedCustomer}
                    setAlert={props.setAlert}
                    handleClose={() => setDisablePeoplePlannerVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                /> : undefined
            }
            {disableAppInsideAcrVisible ?
                <DisableAppInsideAcrModal
                    visible={disableAppInsideAcrVisible}
                    selectedCustomer={props.selectedCustomer}
                    setAlert={props.setAlert}
                    handleClose={() => setDisableAppInsideAcrVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                /> : undefined
            }
            {enableCareRosteringVisible ?
                <EnableCareRosteringModal
                    visible={enableCareRosteringVisible}
                    selectedCustomer={props.selectedCustomer}
                    setAlert={props.setAlert}
                    handleClose={() => setEnableCareRosteringVisible(false)}
                    handleCustomerMenuModalClose={props.handleClose}
                    updateCustomerCareRosteringStatus={props.updateCustomerCareRosteringStatus}
                /> : undefined
            }
        </>
    )

}

export default CustomerMenuModal;
